import { useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * A/B test hook.
 * Resolution order: ?v=a|b in URL → localStorage(testKey) → random 50/50.
 * Persists in localStorage. Fires a "view" event once per page load to /api/ab/track.
 *
 * @param {string} testKey  Unique identifier of the test (e.g. "results_hero_v1")
 * @param {object} extra    Extra payload to send with the events (e.g. { auditId })
 * @returns {{ variant: 'a'|'b', track: (event: string) => void }}
 */
export function useAbVariant(testKey, extra = {}) {
  const [variant, setVariant] = useState('a');
  const trackedView = useRef(false);

  useEffect(() => {
    let v = null;
    try {
      const urlParam = new URLSearchParams(window.location.search).get('v');
      if (urlParam === 'a' || urlParam === 'b') v = urlParam;
    } catch (_) {}
    if (!v) {
      try { v = window.localStorage.getItem(`ab:${testKey}`); } catch (_) {}
    }
    if (v !== 'a' && v !== 'b') {
      v = Math.random() < 0.5 ? 'a' : 'b';
    }
    try { window.localStorage.setItem(`ab:${testKey}`, v); } catch (_) {}
    setVariant(v);
    if (!trackedView.current) {
      trackedView.current = true;
      fetch(`${API_URL}/api/ab/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test: testKey, variant: v, event: 'view', ...extra }),
        keepalive: true,
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testKey]);

  const track = (event) => {
    if (!variant) return;
    fetch(`${API_URL}/api/ab/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ test: testKey, variant, event, ...extra }),
      keepalive: true,
    }).catch(() => {});
  };

  return { variant, track };
}

export default useAbVariant;
